import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOpeningBalance, useJournal } from "api/accountingService";
import {
  ColumnsPanelTrigger,
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  Toolbar,
  ToolbarButton,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Alert, Chip, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Search from "assets/icons/search.gif";
import { plGridLocales } from "utils/muiGridLocales";
import {
  buildFromCsv,
  loadContractorsMap,
  loadJournalFromStorage,
  CONTRACTOR_ACCT_RE,
  DOC_TYPES,
  formatKwota,
  JOURNALS_ROWS_KEY,
  MOCK_DATA,
  CsvPayload,
  JournalData,
  JournalRow,
} from "utils/journalDocuments";
import { FC, Props } from "./typings";
import styles from "./styles.module.scss";

// ── Session cache (przeżywa odmontowanie komponentu) ─────────────────────────

let sessionCache: JournalData | null = null;

function loadFromStorage(): JournalData | null {
  const data = loadJournalFromStorage();
  if (data) sessionCache = data;
  return data;
}

// ── Nakładki ─────────────────────────────────────────────────────────────────

const CustomNoRowsOverlay = () => (
  <div className={styles.empty}>
    <img src={Search} alt="" />
    <div>Brak zapisów w dzienniku księgowań.</div>
  </div>
);

// ── Główny komponent ──────────────────────────────────────────────────────────

const JournalRegister: FC<Props> = ({ setSubtitle }) => {
  const apiRef = useGridApiRef();

  const [data, setData] = useState<JournalData>(
    () => sessionCache ?? loadFromStorage() ?? MOCK_DATA,
  );

  // Dane kontrahentów z API bilansu otwarcia (rok bieżący)
  const { data: boApiData } = useOpeningBalance(new Date().getFullYear());

  // Hydratacja z DB: dziennik zapisany podczas migracji. Gdy brak danych lokalnych
  // (np. po wyczyszczeniu localStorage / na innym urządzeniu) — odtwórz z BE.
  const { data: beJournal } = useJournal();
  useEffect(() => {
    if (!beJournal) return;
    if (localStorage.getItem(JOURNALS_ROWS_KEY)) return; // świeższy import lokalny ma pierwszeństwo
    try {
      const headers = JSON.parse(beJournal.headersJson) as string[];
      const rows = JSON.parse(beJournal.rowsJson) as Record<string, unknown>[];
      if (!headers.length) return;
      const payload: CsvPayload = {
        v: 2,
        headers,
        rows,
        importedAt: Date.parse(beJournal.importedAt) || Date.now(),
        fileName: beJournal.fileName ?? "",
        systemId: beJournal.systemId,
      };
      localStorage.setItem(JOURNALS_ROWS_KEY, JSON.stringify(payload));
      const built = buildFromCsv(payload);
      sessionCache = built;
      setData(built);
    } catch { /* ignore */ }
  }, [beJournal]);

  // Gdy BO załaduje się z API — przebuduj jeśli kolumna Kontrahent jest pusta
  useEffect(() => {
    if (!boApiData?.entries?.length) return;
    const apiMap = new Map<string, string>();
    boApiData.entries
      .filter((e) => e.name && CONTRACTOR_ACCT_RE.test(e.code.toLowerCase()))
      .forEach((e) => apiMap.set(e.code.toLowerCase(), e.name!));
    if (apiMap.size === 0) return;
    // Sprawdź czy bieżące dane mają już kontrahentów
    const currentData = sessionCache ?? data;
    if (currentData.source !== "csv" || !currentData.mappingOk) return;
    if (currentData.docRows.some((r) => r.kontrahent)) return;
    const raw = localStorage.getItem(JOURNALS_ROWS_KEY);
    if (!raw) return;
    try {
      const payload = JSON.parse(raw) as CsvPayload;
      if (payload.v && payload.v >= 2) {
        const built = buildFromCsv(payload, apiMap);
        sessionCache = built;
        setData(built);
      }
    } catch { /* ignore */ }
  }, [boApiData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Przy montowaniu: jeśli dane CSV nie mają kontrahentów, a mapa kontrahentów jest niepusta
  // (np. załadowano BO) — przebuduj z localStorage żeby wypełnić kolumnę Kontrahent
  useEffect(() => {
    if (data.source !== "csv" || !data.mappingOk) return;
    const contractorsMap = loadContractorsMap();
    if (contractorsMap.size === 0) return;
    const hasAnyContractor = data.docRows.some((r) => r.kontrahent);
    if (hasAnyContractor) return;
    const raw = localStorage.getItem(JOURNALS_ROWS_KEY);
    if (!raw) return;
    try {
      const payload = JSON.parse(raw) as CsvPayload;
      if (payload.v && payload.v >= 2) {
        const built = buildFromCsv(payload);
        sessionCache = built;
        setData(built);
      }
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Odbiór danych z MigrationTab przez CustomEvent
  useEffect(() => {
    const handler = (e: Event) => {
      const payload = (e as CustomEvent<CsvPayload>).detail;
      if (!payload?.headers) return;
      const built = buildFromCsv(payload);
      sessionCache = built;
      setData(built);
    };
    window.addEventListener("journals_updated", handler);
    return () => window.removeEventListener("journals_updated", handler);
  }, []);

  // Po aktualizacji kartoteki kontrahentów — przebuduj dane jeśli mamy CSV
  useEffect(() => {
    const handler = () => {
      const raw = localStorage.getItem(JOURNALS_ROWS_KEY);
      if (!raw) return;
      try {
        const payload = JSON.parse(raw) as CsvPayload;
        if (payload.v && payload.v >= 2) {
          const built = buildFromCsv(payload);
          sessionCache = built;
          setData(built);
        }
      } catch { /* ignore */ }
    };
    window.addEventListener("contractors_updated", handler);
    return () => window.removeEventListener("contractors_updated", handler);
  }, []);

  // Stan rozwinięcia — klucze to id wierszy nadrzędnych
  const expandedRef = useRef<Set<string> | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(data.docRows.map((r) => r.id)),
  );

  // Wyczyść dane CSV i wróć do stanu "wczytaj plik"
  const clearCsvData = useCallback(() => {
    sessionCache = null;
    try { localStorage.removeItem(JOURNALS_ROWS_KEY); } catch { /* ignore */ }
    setData(MOCK_DATA);
  }, []);

  // Po zmianie danych (nowy CSV) — rozwiń wszystkie nowe dokumenty
  useEffect(() => {
    const newSet = new Set(data.docRows.map((r) => r.id));
    if (!expandedRef.current || expandedRef.current.size !== newSet.size) {
      setExpanded(newSet);
    }
    expandedRef.current = newSet;
    if (data.source === "csv") {
      if (!data.mappingOk) {
        setSubtitle("Błąd mapowania kolumn");
      } else {
        setSubtitle(`${data.docRows.length} dokumentów${data.fileName ? ` · ${data.fileName}` : ""}`);
      }
    } else {
      setSubtitle(`${data.docRows.length} dokumentów`);
    }
  }, [data, setSubtitle]);

  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll   = useCallback(() => setExpanded(new Set(data.docRows.map((r) => r.id))), [data.docRows]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  // Wiersze widoczne zgodnie ze stanem rozwinięcia
  const visibleRows = useMemo(
    () => data.allRows.filter((r) => r.path.length === 1 || expanded.has(r.path[0])),
    [data.allRows, expanded],
  );

  // ── Kolumny ───────────────────────────────────────────────────────────────
  const columns: GridColDef[] = useMemo(() => [
    // Dedykowana kolumna zwijania/rozwijania — hideable: false
    {
      field: "__expand",
      headerName: "",
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      filterable: false,
      hideable: false,
      groupable: false,
      disableColumnMenu: true,
      disableReorder: true,
      renderHeader: () => null,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as JournalRow;
        if (row.path.length !== 1) return null;
        const isExpanded = expanded.has(row.id);
        return (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); toggleExpand(row.id); }}
            sx={{ color: "#5d49c7" }}
          >
            {isExpanded
              ? <KeyboardArrowDownIcon fontSize="small" />
              : <KeyboardArrowRightIcon fontSize="small" />
            }
          </IconButton>
        );
      },
    },

    // Lp. — numer porządkowy dokumentu
    {
      field: "__lp",
      headerName: "Lp.",
      width: 52,
      minWidth: 52,
      sortable: false,
      filterable: false,
      groupable: false,
      disableColumnMenu: true,
      renderHeader: () => <div className={styles.header}>Lp.</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as JournalRow;
        if (row.path.length !== 1) return null;
        const lp = data.docRows.findIndex((r) => r.id === row.id) + 1;
        return <span className={styles.lpBadge}>{lp}</span>;
      },
    },

    // Typ dokumentu
    {
      field: "typDokumentu",
      headerName: "Typ",
      width: 110,
      sortable: false,
      renderHeader: () => <div className={styles.header}>Typ</div>,
      renderCell: (params: GridRenderCellParams) => {
        const v = params.value as string;
        if (!v) return null;
        const meta = DOC_TYPES[v];
        if (!meta) return <span className={styles.cell}>{v}</span>;
        return (
          <Chip
            label={meta.label}
            size="small"
            sx={{ fontWeight: 600, fontSize: 11, color: meta.color, background: meta.bg, border: `1px solid ${meta.color}33`, height: 20 }}
          />
        );
      },
    },

    // Nr dokumentu
    {
      field: "nrDokumentu",
      headerName: "Nr dokumentu",
      width: 170,
      renderHeader: () => <div className={styles.header}>Nr dokumentu</div>,
      renderCell: (params: GridRenderCellParams) => (
        <div className={styles.cell} title={params.value as string}>{params.value}</div>
      ),
    },

    // Data księgowania
    {
      field: "dataKsiegowania",
      headerName: "Data ks.",
      width: 100,
      renderHeader: () => <div className={styles.header}>Data ks.</div>,
      valueFormatter: (v: string) => {
        if (!v) return "";
        const [y, m, d] = v.split("-");
        return `${d}.${m}.${y}`;
      },
    },

    // Treść / opis
    {
      field: "opis",
      headerName: "Treść",
      flex: 1,
      minWidth: 200,
      renderHeader: () => <div className={styles.header}>Treść</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as JournalRow;
        const isDetail = row.path.length > 1;
        return (
          <div className={`${styles.cell} ${isDetail ? styles.detailOpisCell : ""}`} title={params.value as string}>
            {params.value}
          </div>
        );
      },
    },

    // Kontrahent
    {
      field: "kontrahent",
      headerName: "Kontrahent",
      width: 200,
      minWidth: 120,
      sortable: false,
      renderHeader: () => <div className={styles.header}>Kontrahent</div>,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as JournalRow;
        if (!params.value) return null;
        const isHeader = row.path.length === 1;
        return (
          <div
            className={styles.cell}
            title={params.value as string}
            style={{ color: "#5d49c7", fontWeight: isHeader ? 500 : 400, fontSize: isHeader ? undefined : 12 }}
          >
            {params.value}
          </div>
        );
      },
    },

    // Konto Wn
    {
      field: "kontoWn",
      headerName: "Konto Wn",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderHeader: () => <div className={styles.header}>Konto Wn</div>,
      renderCell: (params: GridRenderCellParams) => {
        const v = params.value as string;
        if (!v) return null;
        return <span className={styles.accountWn}>{v}</span>;
      },
    },

    // Konto Ma
    {
      field: "kontoMa",
      headerName: "Konto Ma",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderHeader: () => <div className={styles.header}>Konto Ma</div>,
      renderCell: (params: GridRenderCellParams) => {
        const v = params.value as string;
        if (!v) return null;
        return <span className={styles.accountMa}>{v}</span>;
      },
    },

    // Kwota
    {
      field: "kwota",
      headerName: "Kwota",
      width: 130,
      type: "number",
      renderHeader: () => <div className={styles.header}>Kwota</div>,
      renderCell: (params: GridRenderCellParams) => {
        const v = params.value as number | null;
        if (v == null) return null;
        const isHeader = (params.row as JournalRow).path.length === 1;
        return (
          <span className={isHeader ? styles.kwotaHeader : styles.kwotaDetail}>
            {formatKwota(v)}
          </span>
        );
      },
    },
  ], [expanded, toggleExpand, data.docRows]);

  // ── Toolbar ───────────────────────────────────────────────────────────────
  const allExpanded = expanded.size === data.docRows.length;

  const JournalToolbar = () => (
    <Toolbar>
      <div className={styles.mergedToolbar}>
        <Tooltip title="Odśwież" placement="bottom">
          <button className={styles.addBtn}>
            <RefreshIcon fontSize="small" />
          </button>
        </Tooltip>

        {data.source !== "mock" && (
          <Tooltip title="Wyczyść dane CSV i wróć do widoku demonstracyjnego" placement="bottom">
            <button className={styles.expandAllBtn} onClick={clearCsvData} style={{ color: "#c62828" }}>
              <DeleteOutlineIcon fontSize="small" />
              <span>Wyczyść</span>
            </button>
          </Tooltip>
        )}

        <Tooltip title={allExpanded ? "Zwiń wszystkie" : "Rozwiń wszystkie"} placement="bottom">
          <button className={styles.expandAllBtn} onClick={allExpanded ? collapseAll : expandAll}>
            {allExpanded
              ? <KeyboardArrowDownIcon fontSize="small" />
              : <KeyboardArrowRightIcon fontSize="small" />
            }
            <span>{allExpanded ? "Zwiń wszystkie" : "Rozwiń wszystkie"}</span>
          </button>
        </Tooltip>

        <div className={styles.spacer} />

        {data.source === "csv" && data.fileName && (
          <span style={{ fontSize: 12, color: "#888", marginRight: 8 }}>
            {data.fileName}
          </span>
        )}

        <Tooltip title="Kolumny" placement="bottom-end">
          <ColumnsPanelTrigger render={<ToolbarButton sx={{ border: "1px solid rgba(0,0,0,0.23)", borderRadius: "4px", padding: "4px 8px", color: "#5f5c71", minWidth: "unset", "&:hover": { borderColor: "#5d49c7", background: "rgba(93,73,199,0.04)", color: "#5d49c7" } }} />}>
            <ViewColumnIcon fontSize="small" />
          </ColumnsPanelTrigger>
        </Tooltip>
      </div>
    </Toolbar>
  );

  // Baner błędu mapowania — pokazuje dostępne kolumny z pliku
  const MappingErrorBanner = () => {
    if (data.source !== "csv" || data.mappingOk !== false) return null;
    const cols = data.detectedCols ?? {};
    const colsText = Object.entries(cols)
      .filter(([k]) => k !== "pairMode")
      .map(([k, v]) => v ? `${k} → "${v}"` : `${k} → (nie znaleziono)`)
      .join(" | ");
    return (
      <Alert
        severity="warning"
        onClose={clearCsvData}
        sx={{ mx: 2, mt: 1, fontSize: 12 }}
      >
        <strong>Nie udało się rozpoznać struktury pliku CSV.</strong><br />
        Sprawdź w konsoli przeglądarki jakie kolumny są dostępne w pliku i porównaj z oczekiwanymi nazwami.<br />
        {colsText && <span style={{ fontFamily: "monospace", fontSize: 11 }}>{colsText}</span>}
        <br />Wczytaj plik ponownie w <strong>Ustawieniach → Budżet i FK → Migracja</strong>.
      </Alert>
    );
  };

  return (
    <div className={styles.wrapper}>
      <MappingErrorBanner />
      <div className={styles.list}>
        <DataGridPremium
          apiRef={apiRef}
          localeText={plGridLocales}
          slots={{ toolbar: JournalToolbar, noRowsOverlay: CustomNoRowsOverlay }}
          showToolbar
          disableRowSelectionOnClick
          columnHeaderHeight={30}
          columns={columns}
          rows={visibleRows}
          hideFooter
          disableColumnFilter
          disablePivoting
          getRowClassName={(params) =>
            (params.row as JournalRow).path.length === 1 ? styles.headerRow : styles.detailRow
          }
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": { outline: "none !important" },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus": { outline: "none !important" },
          }}
        />
      </div>
    </div>
  );
};

export default JournalRegister;
